/** Última ruta SPA (pull-to-refresh en PWA suele volver a `/`). */
export const PWA_LAST_ROUTE_STORAGE_KEY = 'va_pwa_last_path';
export const PWA_LAST_ROUTE_TS_KEY = 'va_pwa_last_path_ts';
/** Marca de `pagehide` no persistente (refresco / cierre). */
export const PWA_HIDE_TS_KEY = 'va_pwa_hide_ts';

const ROOT_PATHS = new Set(['/', '', '/index.html']);
/** Si la última navegación fue hace menos de esto y estamos en `/`, restaurar. */
const ROUTE_RESTORE_MAX_AGE_MS = 10 * 60_000;

function isAtSpaRoot(pathname: string, search: string): boolean {
  return ROOT_PATHS.has(pathname) && !search;
}

function isReloadNavigation(): boolean {
  if (typeof performance.getEntriesByType === 'function') {
    const n = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (n?.type === 'reload') return true;
  }
  const legacy = (performance as unknown as { navigation?: { type: number } }).navigation;
  return legacy?.type === 1;
}

function recentNonBfcacheHide(maxMs: number): boolean {
  const raw = sessionStorage.getItem(PWA_HIDE_TS_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  return Number.isFinite(ts) && Date.now() - ts < maxMs;
}

function recentRouteSnapshot(maxMs: number): boolean {
  const raw = localStorage.getItem(PWA_LAST_ROUTE_TS_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  return Number.isFinite(ts) && Date.now() - ts < maxMs;
}

export function savePwaLastRoute(path: string): void {
  if (!path.startsWith('/')) return;
  try {
    localStorage.setItem(PWA_LAST_ROUTE_STORAGE_KEY, path);
    localStorage.setItem(PWA_LAST_ROUTE_TS_KEY, String(Date.now()));
    sessionStorage.setItem(PWA_LAST_ROUTE_STORAGE_KEY, path);
  } catch {
    /* */
  }
}

function readSavedRoute(): string | null {
  const saved =
    localStorage.getItem(PWA_LAST_ROUTE_STORAGE_KEY) ??
    sessionStorage.getItem(PWA_LAST_ROUTE_STORAGE_KEY);
  if (!saved?.startsWith('/') || saved === '/' || saved === '/index.html') return null;
  return saved;
}

/** ¿Debemos intentar volver a la pantalla anterior tras un refresh en `/`? */
export function getPwaPathToRestoreAfterReload(): string | null {
  try {
    const { pathname, search } = window.location;
    if (!isAtSpaRoot(pathname, search)) return null;

    const saved = readSavedRoute();
    if (!saved) return null;

    const freshRoute = recentRouteSnapshot(ROUTE_RESTORE_MAX_AGE_MS);
    const looksLikeRefresh =
      isReloadNavigation() || recentNonBfcacheHide(30_000) || freshRoute;

    if (!looksLikeRefresh) return null;

    return saved;
  } catch {
    return null;
  }
}

export function clearPwaRestoreMarkers(): void {
  try {
    sessionStorage.removeItem(PWA_HIDE_TS_KEY);
  } catch {
    /* */
  }
}

export function registerPwaPageHideCapture(): void {
  const snapshot = () => {
    try {
      sessionStorage.setItem(PWA_HIDE_TS_KEY, String(Date.now()));
      savePwaLastRoute(`${window.location.pathname}${window.location.search}`);
    } catch {
      /* */
    }
  };

  window.addEventListener('pagehide', (e: PageTransitionEvent) => {
    if (e.persisted) return;
    snapshot();
  });

  window.addEventListener('beforeunload', snapshot);
}

/** Antes de montar React: corrige la URL si el refresh aterrizó en `/`. */
export function tryRestoreSpaPathAfterPwaReload(): void {
  try {
    const saved = getPwaPathToRestoreAfterReload();
    if (!saved) return;
    clearPwaRestoreMarkers();
    window.history.replaceState(null, '', saved);
  } catch {
    /* */
  }
}
